import {DOCUMENT} from '@angular/common';
import {DestroyRef, inject, Injectable, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router} from '@angular/router';
import {filter, fromEvent} from 'rxjs';

const MD_MIN_WIDTH = '(min-width: 768px)';

@Injectable({providedIn: 'root'})
export class MobileNavService {
  private readonly document = inject(DOCUMENT);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly isOpen = signal(false);

  constructor() {
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.close());

    if (typeof window !== 'undefined') {
      const mq = window.matchMedia(MD_MIN_WIDTH);
      const onViewportChange = () => {
        if (mq.matches) {
          this.close();
        }
      };
      mq.addEventListener('change', onViewportChange);
      this.destroyRef.onDestroy(() => mq.removeEventListener('change', onViewportChange));
    }

    if (typeof window !== 'undefined') {
      fromEvent<KeyboardEvent>(this.document, 'keydown')
        .pipe(
          filter(() => this.isOpen()),
          filter((e) => e.key === 'Escape'),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe(() => this.close());
    }
  }

  open(): void {
    this.isOpen.set(true);
    this.syncBodyScrollLock();
  }

  close(): void {
    this.isOpen.set(false);
    this.syncBodyScrollLock();
  }

  toggle(): void {
    this.isOpen.update((v) => !v);
    this.syncBodyScrollLock();
  }

  private syncBodyScrollLock(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.isOpen() && !window.matchMedia(MD_MIN_WIDTH).matches) {
      this.document.body.style.overflow = 'hidden';
    } else {
      this.document.body.style.overflow = '';
    }
  }
}
